/**
 * [PGF Draft Board Refresh v1.1 §F] BoardStyleSelector - navbar popover
 * with three board-style options and live thumbnails.
 *
 * Behavior (§F.1-F.4 + §G.3):
 *   • Reads the resolved style from context (user override > league default
 *     > product default).
 *   • On selection, writes to localStorage (`pgf_board_style:{league_id}`)
 *     for instant re-render, then reconciles with the backend
 *     PUT /api/users/me/board-preference.
 *   • "Use league default" link at bottom clears the per-league override
 *     (surfaced only when an override is set).
 *   • Commissioner mode: the same UI writes to league default via
 *     PUT /api/leagues/{id}/default-board-style. Non-commissioners only
 *     set their per-user override. Choice is inferred from `isCommissioner`.
 *
 * NOTE (§F.3 verdict 9.B correction): user override does NOT broadcast
 * over WS. Only commissioner league-default changes fire BOARD_STYLE_CHANGED
 * (handled server-side).
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { LayoutGrid, Check } from "lucide-react";
import { authHeaders } from "../contexts/AuthContext";
import {
  BOARD_STYLES,
  VALID_BOARD_STYLES,
  BOARD_STYLE_LABELS,
  boardStyleLocalStorageKey,
} from "../utils/boardStyles";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Small SVG thumbnails - recognizably distinct, not pixel-fidelity.
function ThumbnailPGF() {
  return (
    <svg width="64" height="40" viewBox="0 0 64 40" aria-hidden="true">
      <rect width="64" height="40" fill="#0F0F12" />
      {[0, 1, 2, 3].map((r) =>
        [0, 1, 2, 3, 4, 5].map((c) => (
          <rect
            key={`${r}-${c}`}
            x={2 + c * 10}
            y={2 + r * 9}
            width="8"
            height="7"
            rx="1"
            fill={["#E03030", "#00C853", "#007AFF", "#C97000", "#B0B0B0", "#FF6B00"][(r + c) % 6]}
            opacity="0.55"
          />
        )),
      )}
    </svg>
  );
}
function ThumbnailTraditional() {
  return (
    <svg width="64" height="40" viewBox="0 0 64 40" aria-hidden="true">
      <rect width="64" height="40" fill="#E8DEC5" />
      <rect x="0" y="0" width="64" height="10" fill="#1B2C4A" />
      {[10, 20, 30].map((y, ri) =>
        [0, 1, 2, 3, 4, 5].map((c) => (
          <rect
            key={`${y}-${c}`}
            x={c * 10.7}
            y={y}
            width="10"
            height="9"
            fill={["#C4241F", "#1E5AA8", "#111111", "#159B4C", "#F27C1A", "#0F7A3C"][(ri + c) % 6]}
          />
        )),
      )}
    </svg>
  );
}
function ThumbnailWhiteboard() {
  return (
    <svg width="64" height="40" viewBox="0 0 64 40" aria-hidden="true">
      <rect width="64" height="40" fill="#3E2818" />
      <rect x="3" y="3" width="58" height="34" fill="#FFFFFF" />
      <text x="6" y="12" fontSize="4" fontFamily="'Permanent Marker', cursive" fill="#D02020">
        TEAM1 TEAM2 TEAM3
      </text>
      <line x1="5" y1="15" x2="59" y2="15" stroke="#D02020" strokeWidth="0.7" />
      <text x="6" y="24" fontSize="5" fontFamily="'Permanent Marker', cursive" fill="#1A56C4">
        BELL
      </text>
      <text x="24" y="24" fontSize="5" fontFamily="'Permanent Marker', cursive" fill="#B41E1E">
        BRADY
      </text>
    </svg>
  );
}

const THUMBS = {
  [BOARD_STYLES.PGF_STANDARD]: ThumbnailPGF,
  [BOARD_STYLES.TRADITIONAL]: ThumbnailTraditional,
  [BOARD_STYLES.WHITEBOARD]: ThumbnailWhiteboard,
};

export default function BoardStyleSelector({
  leagueId,
  activeStyle,
  userOverride,
  isCommissioner,
  onLocalStyleChange,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const hasOverride =
    userOverride && VALID_BOARD_STYLES.includes(userOverride);

  const applyStyle = async (style) => {
    setOpen(false);
    if (!leagueId) return;
    // Notify parent immediately so the board swaps without waiting for
    // the network round-trip. §F.3 "no page reload."
    onLocalStyleChange?.(style, /*asOverride=*/!isCommissioner);
    // Instant re-render - localStorage first per §G.3. Commissioner path
    // does NOT touch the user-override warm-cache (they're setting the
    // LEAGUE DEFAULT, not their personal override).
    if (!isCommissioner) {
      try {
        localStorage.setItem(boardStyleLocalStorageKey(leagueId), style);
      } catch (_e) { /* storage may be blocked */ }
    }
    // Persist to backend.
    try {
      if (isCommissioner) {
        await axios.put(
          `${BACKEND}/api/leagues/${leagueId}/default-board-style`,
          { default_board_style: style },
          { headers: authHeaders() },
        );
        // Server fires BOARD_STYLE_CHANGED to WS peers automatically.
      } else {
        await axios.put(
          `${BACKEND}/api/users/me/board-preference`,
          { league_id: leagueId, board_style: style },
          { headers: authHeaders() },
        );
      }
    } catch (_e) { /* non-fatal, localStorage already updated */ }
  };

  const clearOverride = async () => {
    setOpen(false);
    if (!leagueId) return;
    try {
      localStorage.removeItem(boardStyleLocalStorageKey(leagueId));
    } catch (_e) { /* ignore */ }
    onLocalStyleChange?.(null, /*asOverride=*/true);
    try {
      await axios.put(
        `${BACKEND}/api/users/me/board-preference`,
        { league_id: leagueId, board_style: null },
        { headers: authHeaders() },
      );
    } catch (_e) { /* non-fatal */ }
  };

  return (
    <div ref={ref} className="relative" data-testid="board-style-selector">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="opacity-75 hover:opacity-100 transition-opacity p-0.5"
        style={{ width: 22, height: 22 }}
        title="Board style"
        aria-label="Board style selector"
        data-testid="board-style-selector-button"
      >
        <LayoutGrid size={16} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 rounded-lg border shadow-xl z-40 overflow-hidden"
          style={{
            width: 260,
            background: "#1A1A1D",
            borderColor: "#2E2E33",
          }}
          data-testid="board-style-selector-popover"
        >
          {VALID_BOARD_STYLES.map((style) => {
            const Thumb = THUMBS[style];
            const isActive = activeStyle === style;
            return (
              <button
                key={style}
                type="button"
                onClick={() => applyStyle(style)}
                data-testid={`board-style-option-${style}`}
                data-active={isActive ? "true" : undefined}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#252530] transition-colors"
              >
                <div className="shrink-0 rounded-sm overflow-hidden border border-[#2E2E33]">
                  <Thumb />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="font-display font-bold text-sm text-[#F5F5F5] leading-tight"
                    style={{ letterSpacing: "0.01em" }}
                  >
                    {BOARD_STYLE_LABELS[style]}
                  </p>
                </div>
                {isActive && <Check size={14} className="text-[#FF6B00] shrink-0" />}
              </button>
            );
          })}
          {hasOverride && (
            <button
              type="button"
              onClick={clearOverride}
              className="w-full text-left px-3 py-2 border-t border-[#2E2E33] text-[11px] font-body text-[#9CA3AF] hover:text-[#F5F5F5] hover:bg-[#252530] transition-colors"
              data-testid="board-style-reset-to-league-default"
            >
              Use league default
            </button>
          )}
        </div>
      )}
    </div>
  );
}
